import { useCallback, useEffect, useRef, useState } from 'react';
import { confirmable, ConfirmDialog, createConfirmation } from 'react-confirm';
import styled from 'styled-components';
import { CustomMusic, CustomMusicManifest, Hymn, SongSlug } from '../../../../common/src/model.ts';
import { getSongSlug } from '../../../../common/src/paths.ts';
import * as server_api from '../../../../common/src/server_api.ts';
import { LocalStorageKey } from '../../data/client_local_storage.ts';
import { useCurrentOrg } from '../../data/use_current_org.tsx';
import { recordRecentlyAddedSong } from '../../data/use_custom_music.tsx';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { alert, confirm } from '../../util/confirm.tsx';
import { getPlatform, Platform } from '../../util/platform.ts';
import { CloseButton } from '../../util/shared.tsx';
import { Spinner } from '../../util/spinner.tsx';
import { useEffectOnce } from '../../util/use_effect_once.ts';
import { useHoldAppReload } from '../../util/use_hold_app_reload.ts';
import { holdAppReload } from '../../util/user_activity.ts';
import { BackButton } from '../admin/org/shared.tsx';
import { Button } from './button.tsx';
import { Message, TextInput } from './common.tsx';
import { Frame } from './frame.tsx';
import { HiddenSongCheckBox, useSongVisibility } from './hidden_song_check_box.tsx';

// see addCustomSong primary entry point at bottom of file

enum UploadStatus {
  Before = 'before',
  Uploading = 'uploading',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

// The setStatus guard is ineffective b/c react invokes the useEffect twice without updating status.
// So, we resort to a global boolean... :(
let busyUploading = false;

// To support drag/drop into a dropzone / drop target in the future:
// https://www.freecodecamp.org/news/formdata-explained/
// "dropzone" is a good search keyword: https://www.npmjs.com/package/react-dropzone-uploader
// see https://medium.com/@blessingmba3/building-a-file-uploader-with-react-11dba6409480

const AddCustomSong: ConfirmDialog<
  { customMusic: CustomMusic; customMusicManifest: CustomMusicManifest | undefined; defaultSongName?: string },
  { songSlug?: SongSlug }
> = ({ customMusic, customMusicManifest, defaultSongName, proceed, show }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (show) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [show, dialogRef]);

  const [songName, setSongName] = useLocalStorage(LocalStorageKey.MusicUploadSongName);
  const songNameIsValid = !songName?.includes('/');
  const [psalmNumber, setPsalmNumber] = useLocalStorage(LocalStorageKey.MusicUploadPsalmNumber);
  const songNameInputRef = useRef<HTMLInputElement>(null);
  const { currentOrg } = useCurrentOrg();
  const fileChooserInputRef = useRef<HTMLInputElement>(null);
  const [requestingPsalmNumber, setRequestingPsalmNumber] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | undefined>();
  const psalmNumberInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<UploadStatus>(UploadStatus.Before);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const [assignedSongNumber, setAssignedSongNumber] = useState<string | undefined>();
  const [isHiddenSong, setIsHiddenSong] = useState(false);
  const { setSongVisibility } = useSongVisibility();

  // A reload would take this dialog's unsaved name and psalm number away, and would abort an upload
  // mid-flight (or skip the manifest bookkeeping that follows it), so hold the app reload off for as
  // long as either is true - the upload outlives the dialog when the user cancels (issue #3265).
  // the dialog holds the reload while it is open; the request below holds it while in flight,
  // because cancelling hides the dialog (and unmounts this) while the upload keeps going
  useHoldAppReload(show);

  useEffectOnce(() => {
    setPsalmNumber(undefined); // clear previously entered psalm number
  });

  useEffect(() => {
    if (requestingPsalmNumber) {
      return;
    }
    songNameInputRef.current?.focus();
  }, [requestingPsalmNumber, songNameInputRef, setPsalmNumber, songName]);

  useEffect(() => {
    if (!requestingPsalmNumber) {
      return;
    }
    psalmNumberInputRef.current?.focus();
  }, [psalmNumberInputRef, requestingPsalmNumber, psalmNumber]);

  const onSongNameChanged = useCallback(() => {
    setSongName(songNameInputRef.current?.value ?? '');
  }, [songNameInputRef, setSongName]);

  const onPsalmNumberChanged = useCallback(() => {
    const value = psalmNumberInputRef.current?.value;
    if (value === undefined) {
      setPsalmNumber(undefined);
    }
    const number = psalmNumberInputRef.current?.value?.match(/(?<number>\d+)/)?.groups?.number;
    if (!number) {
      setPsalmNumber(undefined);
    }
    setPsalmNumber(Number(number));
  }, [psalmNumberInputRef, setPsalmNumber]);

  const onClickUpload = useCallback(() => {
    if (!songNameIsValid) {
      void alert({ confirmation: <span>Song name may not contain a slash (/)</span> });
      return;
    }
    const number = songName?.match(/^Psalm (?<number>\d+)/)?.groups?.number;
    if (number && !psalmNumber) {
      setPsalmNumber(Number(number));
    }
    fileChooserInputRef?.current?.click();
  }, [psalmNumber, setPsalmNumber, songName, songNameIsValid]);

  const onAddPsalmNumber = useCallback(() => {
    setRequestingPsalmNumber(true);
  }, []);

  const onPsalmNumberBack = useCallback(() => {
    setRequestingPsalmNumber(false);
  }, []);

  const onFileChosen = useCallback(() => {
    const file = fileChooserInputRef?.current?.files?.[0];
    if (!file || !currentOrg || !songName) {
      console.log('no file');
      return;
    }
    setRequestingPsalmNumber(false);
    setFileToUpload(file);
  }, [currentOrg, songName]);

  const onClose = useCallback(() => {
    proceed({});
  }, [proceed]);

  const onRetry = useCallback(() => {
    setStatus(UploadStatus.Before);
    setErrorMessage(undefined);
    setFileToUpload(undefined);
    setIsUploadSuccessful(false);
    if (psalmNumber) {
      setRequestingPsalmNumber(true);
    }
  }, [psalmNumber]);

  const onFinish = useCallback(() => {
    if (!currentOrg || !songName) {
      if (!currentOrg) {
        console.error(`Missing org`);
      } else {
        console.error(`Missing song name`);
      }
      proceed({});
      return;
    }
    if (!assignedSongNumber) {
      console.error(`Server should have returned assigned song number`);
    }
    const songNumber = assignedSongNumber ?? '100';
    const songSlug = getSongSlug({ songNumber, orgId: currentOrg.id });
    void (async () => {
      await recordRecentlyAddedSong({
        customMusicManifest,
        org: currentOrg,
        songSlug,
        songName,
        psalmNumber,
      });
      setPsalmNumber(undefined);
      setSongName(undefined);
      proceed({ songSlug });
    })();
  }, [
    assignedSongNumber,
    currentOrg,
    customMusicManifest,
    proceed,
    psalmNumber,
    setPsalmNumber,
    setSongName,
    songName,
  ]);

  const uploadFile = useCallback(async () => {
    if (busyUploading) {
      return;
    }
    busyUploading = true;
    try {
      if (
        status !== UploadStatus.Before ||
        !fileToUpload ||
        !currentOrg ||
        !songName ||
        isUploadSuccessful ||
        errorMessage
      ) {
        return;
      }
      setStatus(UploadStatus.Uploading);
      const releaseAppReloadHold = holdAppReload();
      try {
        const songNumber = songName && (await songNumberToOverwrite(customMusic[currentOrg.id], songName, psalmNumber));
        const newlyAssignedSongNumber = await server_api.uploadOrgCustomMusicFile({
          orgId: currentOrg.id,
          songName,
          psalmNumber,
          file: fileToUpload,
          songNumber,
        });
        setAssignedSongNumber(newlyAssignedSongNumber);
        setStatus(UploadStatus.Succeeded);
        if (isHiddenSong) {
          const songSlug = getSongSlug({ songNumber: newlyAssignedSongNumber, orgId: currentOrg.id });
          await setSongVisibility({ songSlug, isVisible: false });
        }
      } catch (error: any) {
        setErrorMessage(error.message);
        setStatus(UploadStatus.Failed);
      }
      releaseAppReloadHold();
      setFileToUpload(undefined);
      setIsUploadSuccessful(true);
    } finally {
      busyUploading = false;
    }
  }, [
    currentOrg,
    customMusic,
    errorMessage,
    fileToUpload,
    isHiddenSong,
    isUploadSuccessful,
    psalmNumber,
    setSongVisibility,
    songName,
    status,
  ]);

  useEffect(() => {
    void uploadFile();
  }, [uploadFile]);

  const squeezePageHeight = getPlatform() === Platform.Mobile;
  const isValidPsalmNumber = psalmNumber && psalmNumber >= 0 && psalmNumber <= 150;

  const onClickDialog = useCallback(
    (e: any) => {
      const didClickBackdrop = e.currentTarget === e.target;
      if (didClickBackdrop) {
        onClose();
      }
    },
    [onClose],
  );

  const hiddenSongCheckBox = <HiddenSongCheckBox isHiddenSong={isHiddenSong} setHiddenSong={setIsHiddenSong} />;

  return (
    <Dialog ref={dialogRef} onClick={onClickDialog}>
      {fileToUpload || isUploadSuccessful ? (
        <Frame
          embed={true}
          onboardingPage={undefined}
          mainContent={
            status === UploadStatus.Before || status === UploadStatus.Uploading ? (
              <>
                <Message $widthInChars={16}>Uploading file</Message>
                <Spinner />
              </>
            ) : status === UploadStatus.Failed ? (
              <Message $widthInChars={24}>
                File upload failed.
                <br />
                <br />
                {errorMessage ?? ''}
              </Message>
            ) : (
              <Message $widthInChars={24}>
                File upload successful!
                <br />
                <br />
                {isHiddenSong
                  ? 'You can now add this song to events and song lists.'
                  : 'The new song will be visible in your church hymnal within a couple of minutes.'}
              </Message>
            )
          }
          footerContent={
            status === UploadStatus.Before || status === UploadStatus.Uploading ? (
              <Button onClick={onClose}>CANCEL</Button>
            ) : status === UploadStatus.Failed ? (
              <>
                <Button onClick={onRetry}>RETRY</Button>
                <Button onClick={onClose}>CANCEL</Button>
              </>
            ) : (
              <Button onClick={onFinish}>DONE</Button>
            )
          }
          onEnterKeypress={onFinish}
        />
      ) : requestingPsalmNumber ? (
        <Frame
          embed={true}
          onboardingPage={undefined}
          mainContent={
            <>
              <BackButton onClick={onPsalmNumberBack} isAbsolutePosition={true} />
              <input
                type="file"
                id="imgupload"
                style={{ display: 'none' }}
                ref={fileChooserInputRef}
                onChange={onFileChosen}
              />
              <Message $widthInChars={22}>What is the Psalm number?</Message>
              <TextInput
                type="number"
                onChange={onPsalmNumberChanged}
                defaultValue={psalmNumber}
                ref={psalmNumberInputRef}
              />
            </>
          }
          footerContent={
            <>
              <Button onClick={onClickUpload} disabled={!isValidPsalmNumber}>
                UPLOAD
              </Button>
              {hiddenSongCheckBox}
            </>
          }
          onEnterKeypress={onClickUpload}
          containsInputElement={true}
        />
      ) : (
        <Frame
          key="request-song-name"
          embed={true}
          onboardingPage={undefined}
          mainContent={
            <>
              <CloseButton onClick={onClose} isCloseToTop={true} />
              <input
                type="file"
                id="imgupload"
                style={{ display: 'none' }}
                ref={fileChooserInputRef}
                onChange={onFileChosen}
              />
              <Message $widthInChars={22}>What is the name of the song?</Message>
              <CustomMusicInput
                key="song-name"
                type="text"
                onChange={onSongNameChanged}
                ref={songNameInputRef}
                defaultValue={songName ?? defaultSongName}
                $squeezePageHeight={squeezePageHeight}
                $songNameIsValid={songNameIsValid}
              />
              <FinePrint>
                Attention: By uploading music you are indicating that you have the right to distribute this music within
                your church.
              </FinePrint>
            </>
          }
          footerContent={
            <>
              <Button onClick={onClickUpload} disabled={!songName}>
                UPLOAD
              </Button>
              <Button onClick={onAddPsalmNumber} disabled={!songName}>
                ADD PSALM NUMBER
              </Button>
              {hiddenSongCheckBox}
            </>
          }
          onEnterKeypress={onClickUpload}
          containsInputElement={true}
          containsMultipleButtons={true}
        />
      )}
    </Dialog>
  );
};

const CustomMusicInput = styled(TextInput)<{ $squeezePageHeight: boolean; $songNameIsValid: boolean }>`
  padding-top: ${(props) => (props.$squeezePageHeight ? '10px' : '40px')};
  ${(props) => (props.$songNameIsValid ? '' : 'border-color: var(--color-invalid-input)')};
`;

const Dialog = styled.dialog`
  height: 75%;
  border: none;
  border-radius: 20px;
  position: fixed;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 20px;
  margin: auto;
  background: var(--color-background);
  padding: 20px;
  top: 0;
`;

async function songNumberToOverwrite(
  customMusic: Hymn[],
  songName: string,
  psalmNumber?: number,
): Promise<string | undefined> {
  if (!customMusic) {
    return undefined;
  }

  for (const hymn of customMusic) {
    const hymnPsalmNumber = hymn.psalm?.match(/(?<number>\d+)/)?.groups?.number;
    if (
      hymn.title.toLowerCase() === songName.toLowerCase() &&
      !!hymn.psalm === !!psalmNumber &&
      (!psalmNumber || psalmNumber === Number(hymnPsalmNumber))
    ) {
      if (
        await confirm({
          confirmation: <span>Would you like to overwrite the existing song with the same name?</span>,
        })
      ) {
        return hymn.songNumber;
      }
      break;
    }
  }
}

export const addCustomSong = createConfirmation(confirmable(AddCustomSong));

const FinePrint = styled.div`
  margin-top: 2rem;
  font-family: Jost-Regular, sans-serif;
  font-size: 0.6em;
  width: 42ch;
`;
